import { createClient } from '@supabase/supabase-js'

import { env } from '@/lib/env'
import type { Database } from '@/types/database.types'

/**
 * The single Supabase client for the whole application.
 *
 * There must be exactly one. Two instances mean two auth state machines,
 * two token refresh timers, and a class of bug where one half of the app
 * is signed in and the other is not. The ESLint config forbids importing
 * `createClient` anywhere but this file.
 *
 * Typed with the generated `Database` type, so a column rename in a
 * migration becomes a compile error rather than a runtime `undefined`.
 * Regenerate with `npm run db:types` after every migration.
 */
export const supabase = createClient<Database>(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
  auth: {
    // Sessions persist across reloads and refresh silently. A family
    // member browsing photographs should not be logged out mid-scroll.
    persistSession: true,
    autoRefreshToken: true,
    // PKCE rather than the implicit flow: no token in the URL fragment,
    // which matters because invitation links get forwarded around
    // families over email and messaging apps.
    flowType: 'pkce',
    detectSessionInUrl: true,
    storageKey: 'legacy.auth',
  },
  global: {
    headers: {
      'x-application-name': env.VITE_APP_NAME,
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    // Collaboration is a later milestone; a low rate keeps the connection
    // cheap until then.
    params: { eventsPerSecond: 4 },
  },
})

/**
 * Signed URL for a stored object.
 *
 * All buckets are private, so every image in the UI goes through here.
 * The default TTL is deliberately short: a URL that leaks out of a
 * screenshot or a forwarded email should stop working the same hour.
 */
export async function getSignedUrl(
  bucket: string,
  path: string,
  expiresInSeconds = 3600
): Promise<string | null> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresInSeconds)

  if (error) {
    console.error(`Failed to sign ${bucket}/${path}`, error)
    return null
  }

  return data.signedUrl
}
